// Copyright (c) 2025-2026 Motswagae Holding (Pty) Ltd. All Rights Reserved.
// CIPC Registration: 2019/443098/07
// Author: Velly Sipho Phaloane
//
// This source code is the proprietary and confidential property of
// Motswagae Holding (Pty) Ltd, trading as 360Wallet.
// Unauthorized copying, modification, distribution, or use of this
// software, in whole or in part, is strictly prohibited.
//
// Protected under the Copyright Act 98 of 1978 (South Africa)
// and international copyright treaties.
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Switch } from "../components/ui/switch";
import { getBanks, createBank, updateBank, deleteBank } from "../lib/api";
import { toast } from "sonner";
import { Plus, Building2, Search, Edit2, Trash2, Phone, Hash, Wallet, CreditCard, Info } from "lucide-react";

const Banks = () => {
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingBank, setEditingBank] = useState(null);
  
  const [formData, setFormData] = useState({
    bank_code: "",
    bank_name: "",
    bank_type: "both",
    contact_email: "",
    contact_phone: "",
    api_endpoint: "",
    api_key: "",
    format_profile: {
      issuer_type: "VOUCHER",
      reference_type: "generated",
      reference_length: 16,
      pin_length: 4,
      allowed_characters: "alphanumeric",
      expiry_hours: 16,
      allows_partial_withdrawal: false,
      product_name: ""
    }
  });

  // Default issuer type mapping per ATM-Only spec
  const ISSUER_TYPE_DEFAULTS = {
    FNB: { type: "WALLET", product: "FNB eWallet", allowsPartial: true },
    ABSA: { type: "VOUCHER", product: "ABSA CashSend", allowsPartial: false },
    STD: { type: "VOUCHER", product: "Standard Bank Instant Money", allowsPartial: false },
    NED: { type: "VOUCHER", product: "Nedbank Send-iMali", allowsPartial: false },
    CAP: { type: "VOUCHER", product: "Capitec Send Cash", allowsPartial: false }
  };

  const fetchBanks = async () => {
    try {
      const params = {};
      if (filterType !== "all") {
        params.bank_type = filterType;
      }
      const res = await getBanks(params);
      setBanks(res.data);
    } catch (error) {
      toast.error("Failed to load banks");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanks();
  }, [filterType]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingBank) {
        await updateBank(editingBank.id, formData);
        toast.success("Bank updated successfully");
      } else {
        await createBank(formData);
        toast.success("Bank onboarded successfully");
      }
      setIsDialogOpen(false);
      resetForm();
      fetchBanks();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    }
  };

  const handleEdit = (bank) => {
    setEditingBank(bank);
    const defaults = ISSUER_TYPE_DEFAULTS[bank.bank_code] || { type: "VOUCHER", product: "", allowsPartial: false };
    setFormData({
      bank_code: bank.bank_code,
      bank_name: bank.bank_name,
      bank_type: bank.bank_type,
      contact_email: bank.contact_email || "",
      contact_phone: bank.contact_phone || "",
      api_endpoint: "",
      api_key: "",
      format_profile: {
        issuer_type: bank.format_profile?.issuer_type || defaults.type,
        reference_type: bank.format_profile?.reference_type || "generated",
        reference_length: bank.format_profile?.reference_length || 16,
        pin_length: bank.format_profile?.pin_length || 4,
        allowed_characters: bank.format_profile?.allowed_characters || "alphanumeric",
        expiry_hours: bank.format_profile?.expiry_hours || 16,
        allows_partial_withdrawal: bank.format_profile?.allows_partial_withdrawal || defaults.allowsPartial,
        product_name: bank.format_profile?.product_name || defaults.product
      }
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (bank) => {
    if (!window.confirm(`Are you sure you want to deactivate ${bank.bank_name}?`)) {
      return;
    }
    try {
      await deleteBank(bank.id);
      toast.success("Bank deactivated");
      fetchBanks();
    } catch (error) {
      toast.error("Failed to deactivate bank");
    }
  };

  const handleToggleActive = async (bank) => {
    try {
      await updateBank(bank.id, { is_active: !bank.is_active });
      toast.success(`Bank ${bank.is_active ? 'deactivated' : 'activated'}`);
      fetchBanks();
    } catch (error) {
      toast.error("Failed to update bank status");
    }
  };

  const resetForm = () => {
    setFormData({
      bank_code: "",
      bank_name: "",
      bank_type: "both",
      contact_email: "",
      contact_phone: "",
      api_endpoint: "",
      api_key: "",
      format_profile: {
        issuer_type: "VOUCHER",
        reference_type: "generated",
        reference_length: 16,
        pin_length: 4,
        allowed_characters: "alphanumeric",
        expiry_hours: 16,
        allows_partial_withdrawal: false,
        product_name: ""
      }
    });
    setEditingBank(null);
  };

  const filteredBanks = banks.filter(bank => 
    bank.bank_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    bank.bank_code.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const bankTypeLabel = {
    issuer: "Issuer",
    acquirer: "Acquirer",
    both: "Issuer & Acquirer"
  };

  return (
    <div className="space-y-6" data-testid="banks-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-2xl">Banks</h1>
          <p className="text-muted-foreground">Manage issuer and acquirer bank connections</p>
        </div>
        
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="shadow-[0_0_15px_rgba(59,130,246,0.3)]" data-testid="add-bank-btn">
              <Plus className="h-4 w-4 mr-2" />
              Onboard Bank
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{editingBank ? "Edit Bank" : "Onboard New Bank"}</DialogTitle>
              <DialogDescription>
                {editingBank ? "Update bank configuration" : "Add a new bank to the 360Wallet network"}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="bank_code">Bank Code</Label>
                  <Input
                    id="bank_code"
                    placeholder="FNB"
                    value={formData.bank_code}
                    onChange={(e) => setFormData({ ...formData, bank_code: e.target.value.toUpperCase() })}
                    required
                    disabled={!!editingBank}
                    data-testid="bank-code-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bank_type">Bank Type</Label>
                  <Select 
                    value={formData.bank_type} 
                    onValueChange={(value) => setFormData({ ...formData, bank_type: value })}
                  >
                    <SelectTrigger data-testid="bank-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="issuer">Issuer Only</SelectItem>
                      <SelectItem value="acquirer">Acquirer Only</SelectItem>
                      <SelectItem value="both">Both</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="bank_name">Bank Name</Label>
                <Input
                  id="bank_name"
                  placeholder="First National Bank"
                  value={formData.bank_name}
                  onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                  required
                  data-testid="bank-name-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contact_email">Contact Email</Label>
                  <Input
                    id="contact_email"
                    type="email"
                    placeholder="tech@bank.co.za"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    data-testid="contact-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact_phone">Contact Phone</Label>
                  <Input
                    id="contact_phone"
                    placeholder="+27..."
                    value={formData.contact_phone}
                    onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                    data-testid="contact-phone-input"
                  />
                </div>
              </div>

              {/* Bank Format Intelligence Layer */}
              <div className="border border-border rounded-lg p-4 space-y-4 bg-secondary/20">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Hash className="h-4 w-4 text-primary" />
                  Format Profile (Bank Format Intelligence)
                </div>
                
                {/* Issuer Type - ATM-Only Spec Critical Field */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    Issuer Type
                    <span className="text-xs bg-yellow-500/20 text-yellow-400 px-1.5 py-0.5 rounded">Critical</span>
                  </Label>
                  <Select 
                    value={formData.format_profile.issuer_type} 
                    onValueChange={(value) => setFormData({ 
                      ...formData, 
                      format_profile: { 
                        ...formData.format_profile, 
                        issuer_type: value,
                        allows_partial_withdrawal: value === "WALLET"
                      }
                    })}
                  >
                    <SelectTrigger data-testid="issuer-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="WALLET">
                        <div className="flex items-center gap-2">
                          <Wallet className="h-4 w-4 text-green-400" />
                          <span>WALLET (Partial OK)</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="VOUCHER">
                        <div className="flex items-center gap-2">
                          <CreditCard className="h-4 w-4 text-blue-400" />
                          <span>VOUCHER (Full Only)</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <Info className="h-3 w-3" />
                    {formData.format_profile.issuer_type === "WALLET" 
                      ? "Allows partial withdrawals & multiple PINs (FNB eWallet only)" 
                      : "Full amount must be dispensed in one transaction"
                    }
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Product Name</Label>
                  <Input
                    placeholder="e.g., FNB eWallet, ABSA CashSend"
                    value={formData.format_profile.product_name}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      format_profile: { ...formData.format_profile, product_name: e.target.value }
                    })}
                    data-testid="product-name-input"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Reference Type</Label>
                    <Select 
                      value={formData.format_profile.reference_type} 
                      onValueChange={(value) => setFormData({ 
                        ...formData, 
                        format_profile: { ...formData.format_profile, reference_type: value }
                      })}
                    >
                      <SelectTrigger data-testid="ref-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="generated">Generated Code</SelectItem>
                        <SelectItem value="msisdn">MSISDN (Mobile No.)</SelectItem>
                        <SelectItem value="numeric">Numeric Only</SelectItem>
                      </SelectContent>
                    </Select>
                    {formData.format_profile.reference_type === "msisdn" && (
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <Phone className="h-3 w-3" />
                        Mobile number will be used as auth code (e.g., FNB eWallet)
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>PIN Length</Label>
                    <Select 
                      value={formData.format_profile.pin_length.toString()} 
                      onValueChange={(value) => setFormData({ 
                        ...formData, 
                        format_profile: { ...formData.format_profile, pin_length: parseInt(value) }
                      })}
                    >
                      <SelectTrigger data-testid="pin-length-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="4">4 digits (FNB)</SelectItem>
                        <SelectItem value="5">5 digits</SelectItem>
                        <SelectItem value="6">6 digits (Absa)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {formData.format_profile.reference_type !== "msisdn" && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Code Length</Label>
                      <Input
                        type="number"
                        min="6"
                        max="20"
                        value={formData.format_profile.reference_length}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          format_profile: { ...formData.format_profile, reference_length: parseInt(e.target.value) }
                        })}
                        data-testid="code-length-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Characters</Label>
                      <Select 
                        value={formData.format_profile.allowed_characters} 
                        onValueChange={(value) => setFormData({ 
                          ...formData, 
                          format_profile: { ...formData.format_profile, allowed_characters: value }
                        })}
                      >
                        <SelectTrigger data-testid="chars-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="alphanumeric">Alphanumeric</SelectItem>
                          <SelectItem value="numeric">Numeric Only</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Token Expiry (hours)</Label>
                  <Input
                    type="number"
                    min="1"
                    max="72"
                    value={formData.format_profile.expiry_hours}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      format_profile: { ...formData.format_profile, expiry_hours: parseInt(e.target.value) }
                    })}
                    data-testid="expiry-hours-input"
                  />
                </div>
              </div>

              {!editingBank && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="api_endpoint">API Endpoint (Optional)</Label>
                    <Input
                      id="api_endpoint"
                      placeholder="https://api.bank.co.za/v1"
                      value={formData.api_endpoint}
                      onChange={(e) => setFormData({ ...formData, api_endpoint: e.target.value })}
                      data-testid="api-endpoint-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="api_key">API Key (Optional)</Label>
                    <Input
                      id="api_key"
                      type="password"
                      placeholder="sk_live_..."
                      value={formData.api_key}
                      onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
                      data-testid="api-key-input"
                    />
                  </div>
                </>
              )}

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="submit-bank-btn">
                  {editingBank ? "Update Bank" : "Onboard Bank"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card className="bg-card border-border">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search banks..."
                className="pl-10"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="search-banks-input"
              />
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-full sm:w-48" data-testid="filter-type-select">
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="issuer">Issuers Only</SelectItem>
                <SelectItem value="acquirer">Acquirers Only</SelectItem>
                <SelectItem value="both">Both Types</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Banks Table */}
      <Card className="bg-card border-border">
        <CardContent className="p-0">
          <Table className="data-table">
            <TableHeader>
              <TableRow>
                <TableHead>Bank Code</TableHead>
                <TableHead>Bank Name</TableHead>
                <TableHead>Issuer Type</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Transactions</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <div className="animate-pulse text-muted-foreground">Loading banks...</div>
                  </TableCell>
                </TableRow>
              ) : filteredBanks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <div className="flex flex-col items-center gap-2">
                      <Building2 className="h-8 w-8 text-muted-foreground" />
                      <p className="text-muted-foreground">No banks found</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                filteredBanks.map((bank) => {
                  const defaults = ISSUER_TYPE_DEFAULTS[bank.bank_code] || { type: "VOUCHER", product: "" };
                  const issuerType = bank.format_profile?.issuer_type || defaults.type;
                  const productName = bank.format_profile?.product_name || defaults.product;
                  
                  return (
                    <TableRow key={bank.id}>
                      <TableCell>
                        <code className="font-mono text-sm bg-secondary px-2 py-1 rounded">
                          {bank.bank_code}
                        </code>
                      </TableCell>
                      <TableCell>
                        <div>
                          <span className="font-medium">{bank.bank_name}</span>
                          {productName && (
                            <p className="text-xs text-muted-foreground">{productName}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {issuerType === "WALLET" ? (
                            <>
                              <Wallet className="h-4 w-4 text-green-400" />
                              <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">
                                WALLET
                              </span>
                            </>
                          ) : (
                            <>
                              <CreditCard className="h-4 w-4 text-blue-400" />
                              <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded-full">
                                VOUCHER
                              </span>
                            </>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <span className="text-sm text-muted-foreground">
                            {bankTypeLabel[bank.bank_type]}
                          </span>
                          {bank.format_profile?.reference_type === "msisdn" && (
                            <span className="ml-2 text-xs bg-primary/20 text-primary px-1.5 py-0.5 rounded">
                              MSISDN
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="font-mono">{bank.transaction_count}</span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={bank.is_active}
                            onCheckedChange={() => handleToggleActive(bank)}
                            data-testid={`toggle-bank-${bank.bank_code}`}
                          />
                          <span className={bank.is_active ? "text-green-400" : "text-muted-foreground"}>
                            {bank.is_active ? "Active" : "Inactive"}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(bank)}
                            data-testid={`edit-bank-${bank.bank_code}`}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive hover:text-destructive"
                            onClick={() => handleDelete(bank)}
                            data-testid={`delete-bank-${bank.bank_code}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default Banks;
